import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";
import { serverEncryption } from "@/shared/utils/serverEncryption";

interface SaveRegularSSNParams {
  ssn: string;
  userId: number;
}

interface UseSaveRegularSSNResult {
  saveSSN: (params: SaveRegularSSNParams) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to encrypt a user's SSN and store it in Supabase users table
 * This handles the full flow of:
 * 1. Encrypting the SSN using server-side encryption with user context
 * 2. Updating the users table in Supabase with ssn_encrypted
 */
export function useSaveRegularSSN(): UseSaveRegularSSNResult {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ ssn, userId }: SaveRegularSSNParams) => {
      if (!userId) {
        throw new Error("User ID is required");
      }

      const encryptedSSN = await serverEncryption.encrypt(ssn, "user", userId, "ssn");

      const { error } = await supabase
        .from("users")
        .update({ ssn_encrypted: encryptedSSN })
        .eq("user_id", userId);

      if (error) {
        console.error("Database error saving SSN:", error);
        throw new Error(`Failed to save encrypted SSN: ${error.message}`);
      }
    },
    onSuccess: (_, { userId }) => {
      // Invalidate regular SSN data query to refetch the updated data
      queryClient.invalidateQueries({ queryKey: ["regularSSNData", userId] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });

  return {
    saveSSN: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error?.message || null,
  };
}
